import { widgetCodeSchema } from '@framework/engine/schema/widgets/validation/widgetCodeSchema.tsx';
import { yup } from '@framework/validation/initYup.ts';

/**
 * Singola coppia chiave/valore personalizzata. Il codice è obbligatorio, il
 * valore è opzionale; la configurazione diventa `null` quando tutte le coppie
 * sono vuote (vedi transform di `CustomPropertiesDirectiveSchema`).
 */
export interface ICustomProperty {
  key: string;
  value?: string | null;
}

/**
 * Configurazione della direttiva: elenco di coppie chiave/valore personalizzate
 * serializzato come `widget.directives.customProperties.pairs`.
 */
export interface ICustomPropertiesDirective {
  pairs?: ICustomProperty[];
}

const customPropertySchema: yup.ObjectSchema<ICustomProperty> = yup
  .object({
    // Codice obbligatorio (riusa il validatore canonico): ogni coppia presente
    // deve avere un codice valido. La config si azzera solo quando TUTTE le
    // coppie sono vuote (vedi transform).
    key: widgetCodeSchema,
    value: yup.string().nullable().label('Valore'),
  })
  .noUnknown();

export const CustomPropertiesDirectiveSchema: yup.ObjectSchema<ICustomPropertiesDirective> =
  yup
    .object({
      pairs: yup.array().of(customPropertySchema),
    })
    /**
     * Se TUTTE le coppie sono vuote ritorna `undefined`: il wrapper
     * `WidgetDirectivesSchema` lo normalizza poi a `null`, cioè
     * `directives.customProperties: null` (come ogni direttiva non
     * configurata, es. `tableSave`/`applicantData`).
     * Altrimenti lascia l'array **invariato**: non filtra le singole coppie
     * vuote, per non reindicizzare l'array e disallineare gli errori di
     * validazione dalle righe del form (le coppie vuote residue vengono
     * segnalate come "codice obbligatorio").
     */
    .transform((value: ICustomPropertiesDirective | null | undefined) => {
      if (value == null) return value;
      const allEmpty = (value.pairs ?? []).every(
        pair => (pair?.key ?? '') === '' && (pair?.value ?? '') === '',
      );
      return allEmpty ? undefined : value;
    })
    .test(
      'unique-keys',
      'Le chiavi delle proprietà personalizzate devono essere univoche',
      function (value) {
        const seen = new Set<string>();
        // Indice della prima chiave duplicata (seconda occorrenza), ignorando
        // le chiavi vuote.
        const duplicateIndex = (value?.pairs ?? []).findIndex(pair => {
          const key = pair?.key;
          if (!key) return false;
          if (seen.has(key)) return true;
          seen.add(key);
          return false;
        });
        if (duplicateIndex === -1) return true;
        // Attacca l'errore al campo `key` della coppia duplicata, così è
        // visibile sul FormItem corrispondente (non sul path della direttiva).
        const basePath = this.path ? `${this.path}.` : '';
        return this.createError({
          path: `${basePath}pairs.${duplicateIndex}.key`,
        });
      },
    )
    .noUnknown();
